"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AppBaseNavigationItemSchema = exports.AppBaseCompiledTopBarItemSchema = exports.AppTopBarItemSchema = exports.AppNavigationPathSchema = exports.AppNavigationLabelsSchema = exports.AppNavigationLabelSchema = exports.AppNavigationIconSchema = exports.AppBaseNavigationBadgeSchema = void 0;
exports.appNavigationPrimaryIssue = appNavigationPrimaryIssue;
exports.appNavigationIssues = appNavigationIssues;
exports.appNavigationFileIssues = appNavigationFileIssues;
const zod_1 = require("zod");
const app_base_bindings_1 = require("./app-base-bindings");
const app_base_html_document_1 = require("./app-base-html-document");
/** Counts the authorized rows of a declared dataset, with the list's literal filter. */
exports.AppBaseNavigationBadgeSchema = zod_1.z
    .object({
    sourceKey: zod_1.z.string().min(1).max(128),
    filter: zod_1.z
        .object({
        field: app_base_bindings_1.AppBaseBindingFieldSchema,
        key: zod_1.z
            .string()
            .min(1)
            .max(256)
            .refine(key => key.trim().length > 0),
    })
        .strict()
        .optional(),
})
    .strict();
exports.AppNavigationIconSchema = zod_1.z
    .string()
    .refine(path => path.endsWith('.svg') &&
    !/[\\:%?#\u0000-\u0020]/.test(path) &&
    path
        .split('/')
        .every(part => part !== '' && part !== '.' && part !== '..'), 'Navigation icon must be a relative SVG file path');
exports.AppNavigationLabelSchema = zod_1.z.string().min(1).max(80);
exports.AppNavigationLabelsSchema = zod_1.z.record(zod_1.z.string().min(1), zod_1.z.string().min(1));
exports.AppNavigationPathSchema = zod_1.z.string().startsWith('/');
const AppTopBarItemBaseSchema = zod_1.z
    .object({
    id: zod_1.z.string().regex(/^[A-Za-z][A-Za-z0-9_-]{0,63}$/),
    label: exports.AppNavigationLabelSchema,
    labelsI18n: exports.AppNavigationLabelsSchema.optional(),
    icon: exports.AppNavigationIconSchema,
    order: zod_1.z.number().int().nonnegative().default(0),
    path: exports.AppNavigationPathSchema.optional(),
    panel: zod_1.z
        .object({ entry: zod_1.z.string().min(1) })
        .strict()
        .optional(),
    badge: exports.AppBaseNavigationBadgeSchema.optional(),
})
    .strict();
const topBarTarget = (item, ctx) => {
    if ((item.path !== undefined) === (item.panel !== undefined))
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['panel'],
            message: 'Top bar entry requires exactly one of path and panel',
        });
};
exports.AppTopBarItemSchema = AppTopBarItemBaseSchema.superRefine(topBarTarget);
exports.AppBaseCompiledTopBarItemSchema = AppTopBarItemBaseSchema.extend({
    id: zod_1.z.string().min(1),
}).superRefine(topBarTarget);
exports.AppBaseNavigationItemSchema = zod_1.z
    .object({
    id: zod_1.z.string().min(1),
    label: exports.AppNavigationLabelSchema,
    labelsI18n: exports.AppNavigationLabelsSchema.optional(),
    path: exports.AppNavigationPathSchema.optional(),
    action: zod_1.z
        .object({
        type: zod_1.z.enum(['overlay', 'anchor']),
        target: zod_1.z.string().regex(/^[A-Za-z][A-Za-z0-9_:.-]*$/),
    })
        .strict()
        .optional(),
    icon: exports.AppNavigationIconSchema.optional(),
    badge: exports.AppBaseNavigationBadgeSchema.optional(),
    primary: zod_1.z.boolean().optional(),
    order: zod_1.z.number().int().nonnegative().default(0),
    /** Set by composition from the mount's navigationPlacement. */
    placement: zod_1.z.enum(['primary', 'secondary']).optional(),
    /** Set by composition: the start-page entry of the same App. */
    parentId: zod_1.z.string().min(1).optional(),
})
    .strict()
    .superRefine((item, ctx) => {
    if ((item.path !== undefined) === (item.action !== undefined)) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['action'],
            message: 'Navigation requires exactly one of path and action',
        });
    }
});
/** Five slots include More when primary and non-primary entries coexist. */
function appNavigationPrimaryIssue(navigation) {
    const primary = navigation.filter(item => item.primary);
    const slots = primary.length +
        (primary.length > 0 && primary.length < navigation.length ? 1 : 0);
    if (slots > 5)
        return {
            path: ['navigation'],
            message: 'An App allows at most 5 navigation slots (including More for non-primary entries): ' +
                primary.map(item => item.id).join(', '),
        };
    return undefined;
}
/** Shared by App definition checks and the final merged Container manifest. */
function appNavigationIssues(navigation, sourceKeys, context, section = 'navigation') {
    const issues = [];
    const primaryIssue = appNavigationPrimaryIssue(navigation);
    if (section === 'navigation' && primaryIssue)
        issues.push(primaryIssue);
    const sources = new Set(sourceKeys);
    navigation.forEach((item, index) => {
        const badge = item.badge;
        if (!badge)
            return;
        const at = [section, index, 'badge'];
        if (!sources.has(badge.sourceKey)) {
            issues.push({
                path: [...at, 'sourceKey'],
                message: 'Navigation badge ' +
                    item.id +
                    ' references undeclared dataset ' +
                    badge.sourceKey,
            });
        }
        // A shared frame renders this badge on every page it wraps.
        if (context?.frame) {
            const frame = context.frame;
            for (const route of context.routes ?? []) {
                if (frame.dataSourceKeys.includes(badge.sourceKey) ||
                    route.dataSourceKeys.includes(badge.sourceKey))
                    continue;
                issues.push({
                    path: [...at, 'sourceKey'],
                    message: 'Navigation badge ' +
                        item.id +
                        ' dataset ' +
                        badge.sourceKey +
                        ' is not declared in the frame or framed route ' +
                        route.path,
                });
            }
        }
        if (context?.columnsBySource &&
            badge.filter &&
            badge.filter.field !== '@id' &&
            !context.columnsBySource[badge.sourceKey]?.some(column => column.id === badge.filter?.field)) {
            issues.push({
                path: [...at, 'filter', 'field'],
                message: 'Navigation badge ' +
                    item.id +
                    ' filter field ' +
                    badge.filter.field +
                    ' is not exposed by dataset ' +
                    badge.sourceKey,
            });
        }
    });
    return issues;
}
/** A file reference has the same meaning before and after mount qualification. */
function appNavigationFileIssues(navigation, files, documentPaths) {
    const issues = navigation.flatMap((item, index) => {
        if (!item.icon)
            return [];
        const file = files[item.icon];
        return file?.path === item.icon && file.contentType === 'image/svg+xml'
            ? []
            : [
                {
                    path: ['navigation', index, 'icon'],
                    message: `Navigation icon ${item.icon} must reference a declared SVG file`,
                },
            ];
    });
    const documents = [...new Set(documentPaths)].map(path => files[path]);
    // Stored bodies are checked when hydrated. Entity-encoded ids cannot be
    // compared literally; the runtime reports a missing target on activation.
    if (navigation.some(item => item.action) &&
        documents.every(file => file?.text !== undefined)) {
        const ids = documents.flatMap(file => [...(0, app_base_html_document_1.appHtmlElements)(file.text ?? '')]
            .map(element => element.attributes.get('id'))
            .filter((id) => id !== undefined));
        if (!ids.some(id => id.includes('&'))) {
            navigation.forEach((item, index) => {
                if (item.action && !ids.includes(item.action.target))
                    issues.push({
                        path: ['navigation', index, 'action', 'target'],
                        message: 'Navigation entry ' +
                            item.id +
                            ' target ' +
                            item.action.target +
                            ' is missing from the App pages and frame',
                    });
            });
        }
    }
    return issues;
}
